// Service API utilisant Supabase directement depuis le frontend
// Toutes les requêtes utilisent automatiquement le token de session Supabase Auth
import { supabase } from './supabaseClient';

export const api = {
  // Authentification (maintenant gérée directement par Supabase Auth dans AuthContext)
  auth: {
    getProfile: async (userId: string) => {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, name, role, organization, created_at')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return { user: data };
    },
  },

  // Projets
  projects: {
    getAll: async () => {
      console.log('Récupération de tous les projets...');
      
      // Vérifier la session avant de récupérer
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session || !session.user) {
        console.warn('Aucune session active, les projets peuvent ne pas être accessibles');
        throw new Error('Vous devez être connecté');
      }
      
      const userId = session.user.id;
      
      // Récupérer le rôle de l'utilisateur
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();
      
      if (userError) {
        console.error('Erreur lors de la récupération du rôle:', userError);
      }
      
      const userRole = userData?.role;
      
      // Construire la requête selon le rôle
      let query = supabase
        .from('projects')
        .select(`
          *,
          created_by_user:users!projects_created_by_fkey(id, name),
          assigned_to_user:users!projects_assigned_to_fkey(id, name),
          manager_agence_user:users!projects_manager_agence_fkey(id, name),
          contact_soebra_user:users!projects_contact_soebra_fkey(id, name)
        `);
      
      // Les agents terrain ne voient que les projets où ils sont assignés
      if (userRole === 'agent_terrain') {
        query = query.or(`assigned_to.eq.${userId},created_by.eq.${userId}`);
      }
      // Les admins, agences et SOBEBRA voient tous les projets
      // (pas de filtre supplémentaire)
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des projets:', error);
        console.error('Code:', error.code);
        console.error('Message:', error.message);
        console.error('Détails:', error.details);
        throw error;
      }
      
      console.log('Projets récupérés:', data?.length || 0);
      return { projects: data || [] };
    },

    getById: async (id: string) => {
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select(`
          *,
          created_by_user:users!projects_created_by_fkey(id, name),
          assigned_to_user:users!projects_assigned_to_fkey(id, name)
        `)
        .eq('id', id)
        .single();

      if (projectError) throw projectError;

      const { data: tasks } = await supabase
        .from('tasks')
        .select(`
          *,
          assigned_to_user:users!tasks_assigned_to_fkey(id, name)
        `)
        .eq('project_id', id)
        .order('created_at', { ascending: false });

      return {
        project: {
          ...project,
          created_by_name: project.created_by_user?.name,
          assigned_to_name: project.assigned_to_user?.name,
        },
        tasks: tasks || [],
      };
    },

    create: async (data: {
      title: string;
      description?: string;
      priority?: string;
      assigned_to?: string;
      due_date?: string;
      objectives?: string;
      manager_agence?: string;
      contact_soebra?: string;
      start_date?: string;
      budget?: number;
      zones?: string[];
      location?: string;
    }) => {
      // Récupérer l'utilisateur actuel depuis la session Supabase
      // Essayer d'abord avec getSession() qui est plus fiable
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        // Fallback sur getUser() si getSession() échoue
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          console.error('Erreur d\'authentification:', authError);
          throw new Error('Vous devez être connecté pour créer un projet');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      console.log('Création projet avec userId:', userId);
      console.log('Données projet:', data);
      
      const { data: project, error } = await supabase
        .from('projects')
        .insert({
          title: data.title,
          description: data.description || null,
          priority: data.priority || 'medium',
          status: 'pending',
          assigned_to: data.assigned_to || null,
          due_date: data.due_date || null,
          objectives: data.objectives || null,
          manager_agence: data.manager_agence || null,
          contact_soebra: data.contact_soebra || null,
          start_date: data.start_date || null,
          budget: data.budget || null,
          zones: data.zones || null,
          location: data.location || null,
          created_by: userId,
        })
        .select(`
          *,
          created_by_user:users!projects_created_by_fkey(id, name),
          assigned_to_user:users!projects_assigned_to_fkey(id, name),
          manager_agence_user:users!projects_manager_agence_fkey(id, name),
          contact_soebra_user:users!projects_contact_soebra_fkey(id, name)
        `)
        .single();

      if (error) {
        console.error('Erreur lors de la création du projet:', error);
        console.error('Détails de l\'erreur:', JSON.stringify(error, null, 2));
        throw error;
      }
      
      console.log('Projet créé avec succès:', project);
      return { message: 'Projet créé avec succès', project };
    },

    update: async (id: string, data: any) => {
      const { data: project, error } = await supabase
        .from('projects')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { message: 'Projet mis à jour avec succès', project };
    },

    delete: async (id: string) => {
      const { error } = await supabase.from('projects').delete().eq('id', id);
      if (error) throw error;
      return { message: 'Projet supprimé avec succès' };
    },
  },

  // Tâches
  tasks: {
    create: async (projectId: string, data: {
      title: string;
      description?: string;
      assigned_to?: string;
      due_date?: string;
    }) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      const { data: task, error } = await supabase
        .from('tasks')
        .insert({
          ...data,
          project_id: projectId,
          created_by: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return { message: 'Tâche créée avec succès', task };
    },

    update: async (taskId: string, data: any) => {
      const { data: task, error } = await supabase
        .from('tasks')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', taskId)
        .select(`
          *,
          assigned_to_user:users!tasks_assigned_to_fkey(id, name)
        `)
        .single();

      if (error) throw error;
      return { message: 'Tâche mise à jour avec succès', task };
    },

    delete: async (projectId: string, taskId: string) => {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)
        .eq('project_id', projectId);
      if (error) throw error;
      return { message: 'Tâche supprimée avec succès' };
    },
  },

  // Messages
  messages: {
    getAll: async () => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:users!messages_sender_id_fkey(id, name),
          recipient:users!messages_recipient_id_fkey(id, name),
          project:projects(id, title)
        `)
        .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return {
        messages:
          data?.map((msg: any) => ({
            ...msg,
            sender_name: msg.sender?.name,
            recipient_name: msg.recipient?.name,
            project_title: msg.project?.title,
          })) || [],
      };
    },

    send: async (data: {
      recipient_id: string;
      project_id?: string;
      subject: string;
      content: string;
    }) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      const { data: message, error } = await supabase
        .from('messages')
        .insert({
          ...data,
          sender_id: userId,
        })
        .select()
        .single();

      if (error) throw error;

      // Créer une notification
      await supabase.from('notifications').insert({
        user_id: data.recipient_id,
        type: 'message',
        title: 'Nouveau message',
        message: `Vous avez reçu un message: ${data.subject}`,
        link: `/messages/${message.id}`,
      });

      return { message: 'Message envoyé avec succès', messageData: message };
    },

    delete: async (id: string) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      // Vérifier que l'utilisateur est le destinataire ou l'expéditeur
      const { data: message, error: fetchError } = await supabase
        .from('messages')
        .select('sender_id, recipient_id')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (message.sender_id !== userId && message.recipient_id !== userId) {
        throw new Error('Vous n\'avez pas la permission de supprimer ce message');
      }

      const { error } = await supabase.from('messages').delete().eq('id', id);
      if (error) throw error;
      return { message: 'Message supprimé avec succès' };
    },
  },

  // Notifications
  notifications: {
    getAll: async () => {
      // Utiliser getSession() qui est plus fiable
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          throw new Error('Vous devez être connecté');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return { notifications: data || [] };
    },

    markAsRead: async (id: string) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;
      return { message: 'Notification marquée comme lue' };
    },

    getUnreadCount: async () => {
      // Utiliser getSession() qui est plus fiable
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          throw new Error('Non authentifié');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;
      return { count: count || 0 };
    },

    create: async (data: {
      user_id: string;
      type: string;
      title: string;
      message: string;
      link?: string;
      sender_id?: string;
    }) => {
      // Récupérer l'utilisateur actuel comme sender_id si non fourni
      let senderId = data.sender_id;
      if (!senderId) {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session || !session.user) {
          const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
          if (authError || !authUser) {
            throw new Error('Vous devez être connecté');
          }
          senderId = authUser.id;
        } else {
          senderId = session.user.id;
        }
      }

      const { data: notification, error } = await supabase
        .from('notifications')
        .insert({
          user_id: data.user_id,
          sender_id: senderId,
          type: data.type,
          title: data.title,
          message: data.message,
          link: data.link || null,
        })
        .select()
        .single();

      if (error) throw error;
      return { message: 'Notification créée avec succès', notification };
    },

    getSent: async () => {
      // Utiliser getSession() qui est plus fiable
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          throw new Error('Vous devez être connecté');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          recipient:users!notifications_user_id_fkey(id, name, email)
        `)
        .eq('sender_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return { notifications: data || [] };
    },
  },

  // Rapports terrain
  reports: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('field_reports')
        .select(`
          *,
          created_by_user:users!field_reports_created_by_fkey(id, name),
          approved_by_user:users!field_reports_approved_by_fkey(id, name),
          project:projects(id, title)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Récupérer les pièces jointes
      const reportsWithAttachments = await Promise.all(
        (data || []).map(async (report: any) => {
          const { data: attachments } = await supabase
            .from('report_attachments')
            .select('*')
            .eq('report_id', report.id);

          return {
            ...report,
            created_by_name: report.created_by_user?.name,
            approved_by_name: report.approved_by_user?.name,
            project_title: report.project?.title,
            attachments: attachments || [],
          };
        })
      );

      return { reports: reportsWithAttachments };
    },

    create: async (data: {
      project_id: string;
      title: string;
      description?: string;
      location?: string;
      latitude?: number;
      longitude?: number;
      status?: string;
    }) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      const { data: report, error } = await supabase
        .from('field_reports')
        .insert({
          ...data,
          created_by: userId,
          submitted_at: data.status === 'submitted' ? new Date().toISOString() : null,
        })
        .select()
        .single();

      if (error) throw error;
      return { message: 'Rapport créé avec succès', report };
    },

    update: async (id: string, data: any) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      const updateData: any = {
        ...data,
        updated_at: new Date().toISOString(),
      };

      if (data.status === 'approved' || data.status === 'rejected') {
        updateData.approved_by = userId;
        updateData.approved_at = new Date().toISOString();
      }

      const { data: report, error } = await supabase
        .from('field_reports')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { message: 'Rapport mis à jour avec succès', report };
    },

    getById: async (id: string) => {
      const { data: report, error } = await supabase
        .from('field_reports')
        .select(`
          *,
          created_by_user:users!field_reports_created_by_fkey(id, name),
          approved_by_user:users!field_reports_approved_by_fkey(id, name),
          project:projects(id, title)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Récupérer les pièces jointes
      const { data: attachments } = await supabase
        .from('report_attachments')
        .select('*')
        .eq('report_id', id);

      return {
        report: {
          ...report,
          created_by_name: report.created_by_user?.name,
          approved_by_name: report.approved_by_user?.name,
          project_title: report.project?.title,
          attachments: attachments || [],
        },
      };
    },

    delete: async (id: string) => {
      const { error } = await supabase.from('field_reports').delete().eq('id', id);
      if (error) throw error;
      return { message: 'Rapport supprimé avec succès' };
    },
  },

  // Formulaires
  forms: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('data_forms')
        .select(`
          *,
          created_by_user:users!data_forms_created_by_fkey(id, name)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return {
        forms:
          data?.map((form: any) => ({
            ...form,
            created_by_name: form.created_by_user?.name,
          })) || [],
      };
    },

    submit: async (formId: string, formData: any) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }
      const userId = authUser.id;

      const { data: submission, error } = await supabase
        .from('form_submissions')
        .insert({
          form_id: formId,
          submitted_by: userId,
          data: formData,
        })
        .select()
        .single();

      if (error) throw error;
      return { message: 'Données soumises avec succès', submission };
    },

    getSubmissions: async () => {
      // Récupérer le rôle et le code agent de l'utilisateur
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          throw new Error('Vous devez être connecté');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      // Récupérer le rôle et le code agent
      const { data: userData } = await supabase
        .from('users')
        .select('role, agent_code')
        .eq('id', userId)
        .single();

      const userRole = userData?.role;
      const agentCode = userData?.agent_code;

      let query = supabase
        .from('form_submissions')
        .select(`
          *,
          form:data_forms(id, name),
          submitted_by_user:users!form_submissions_submitted_by_fkey(id, name)
        `);

      // Si c'est un agent terrain, filtrer par code agent dans les données JSONB
      if (userRole === 'agent_terrain' && agentCode) {
        // Filtrer les soumissions où le code agent dans les données correspond
        // Le code agent est stocké dans data->answers->agentCode ou data->metadata->agentCode
        // Utiliser contains pour rechercher dans le JSONB
        query = query.or(`data->answers->>agentCode.eq.${agentCode},data->metadata->>agentCode.eq.${agentCode}`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return {
        submissions:
          data?.map((sub: any) => ({
            ...sub,
            form_name: sub.form?.name,
            submitted_by_name: sub.submitted_by_user?.name,
          })) || [],
      };
    },

    create: async (data: {
      name: string;
      description?: string;
      form_schema: any;
    }) => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        throw new Error('Vous devez être connecté');
      }

      const { data: form, error } = await supabase
        .from('data_forms')
        .insert({
          name: data.name,
          description: data.description || null,
          created_by: authUser.id,
          form_schema: data.form_schema,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return { message: 'Formulaire créé avec succès', form };
    },

    delete: async (id: string) => {
      const { error } = await supabase
        .from('data_forms')
        .update({ is_active: false })
        .eq('id', id);
      if (error) throw error;
      return { message: 'Formulaire désactivé avec succès' };
    },

    deleteSubmission: async (id: string) => {
      const { error } = await supabase
        .from('form_submissions')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { message: 'Soumission supprimée avec succès' };
    },
  },

  // Collaborateurs
  collaborators: {
    getAll: async () => {
      // Simplifier la requête pour éviter l'erreur 406
      const { data, error } = await supabase
        .from('collaborators')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des collaborateurs:', error);
        throw error;
      }
      
      // Charger les données utilisateur séparément si nécessaire
      return { collaborators: data || [] };
    },

    getByUserId: async (userId: string) => {
      // Simplifier la requête pour éviter l'erreur 406
      const { data, error } = await supabase
        .from('collaborators')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erreur lors de la récupération du collaborateur:', error);
        throw error;
      }
      return { collaborator: data || null };
    },

        create: async (data: {
          user_id: string;
          custom_role?: string;
          permissions?: string[];
          sections_access?: string[];
          notes?: string;
        }) => {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const userId = authUser?.id;
          if (!userId) throw new Error('Non authentifié');

          const { data: collaborator, error } = await supabase
            .from('collaborators')
            .insert({
              ...data,
              added_by: userId,
              permissions: data.permissions || [],
              sections_access: data.sections_access || [],
            })
        .select()
        .single();

      if (error) throw error;
      return { message: 'Collaborateur ajouté avec succès', collaborator };
    },

    update: async (id: string, data: {
      custom_role?: string;
      permissions?: string[];
      sections_access?: string[];
      is_active?: boolean;
      notes?: string;
    }) => {
      const { data: collaborator, error } = await supabase
        .from('collaborators')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { message: 'Collaborateur mis à jour avec succès', collaborator };
    },

    delete: async (id: string) => {
      const { error } = await supabase.from('collaborators').delete().eq('id', id);
      if (error) throw error;
      return { message: 'Collaborateur supprimé avec succès' };
    },
  },

  // Utilisateurs
  users: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, role, organization, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { users: data || [] };
    },
  },

  // Inventaire
  inventory: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('inventory_assets')
        .select(`
          *,
          assigned_to_user:users!inventory_assets_assigned_to_fkey(id, name, email),
          created_by_user:users!inventory_assets_created_by_fkey(id, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { assets: data || [] };
    },

    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('inventory_assets')
        .select(`
          *,
          assigned_to_user:users!inventory_assets_assigned_to_fkey(id, name, email),
          created_by_user:users!inventory_assets_created_by_fkey(id, name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return { asset: data };
    },

    create: async (data: {
      name: string;
      description?: string;
      serial_number?: string;
      qr_code?: string;
      category: string;
      status?: string;
      location?: string;
      assigned_to?: string;
      purchase_date?: string;
      purchase_price?: number;
      current_value?: number;
      maintenance_due_date?: string;
      notes?: string;
    }) => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          throw new Error('Vous devez être connecté');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      const { data: asset, error } = await supabase
        .from('inventory_assets')
        .insert({
          ...data,
          created_by: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return { message: 'Ressource ajoutée avec succès', asset };
    },

    update: async (id: string, data: any) => {
      const { data: asset, error } = await supabase
        .from('inventory_assets')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { message: 'Ressource mise à jour avec succès', asset };
    },

    delete: async (id: string) => {
      const { error } = await supabase
        .from('inventory_assets')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { message: 'Ressource supprimée avec succès' };
    },
  },

  // Base de connaissances
  knowledge: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select(`
          *,
          uploaded_by_user:users!knowledge_documents_uploaded_by_fkey(id, name),
          approved_by_user:users!knowledge_documents_approved_by_fkey(id, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { documents: data || [] };
    },

    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select(`
          *,
          uploaded_by_user:users!knowledge_documents_uploaded_by_fkey(id, name),
          approved_by_user:users!knowledge_documents_approved_by_fkey(id, name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return { document: data };
    },

    create: async (data: {
      title: string;
      description?: string;
      category: string;
      folder?: string;
      file_name: string;
      file_path: string;
      file_type?: string;
      file_size?: number;
      access_level?: string;
      tags?: string[];
      notes?: string;
    }) => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          throw new Error('Vous devez être connecté');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      // Vérifier si l'utilisateur est admin
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      const isAdmin = userData?.role === 'admin';

      const insertData: any = {
        ...data,
        tags: data.tags || [],
        uploaded_by: userId,
        status: isAdmin ? 'approved' : 'pending', // Auto-approuver si admin
      };

      // Si admin, auto-approuver le document
      if (isAdmin) {
        insertData.approved_by = userId;
        insertData.approved_at = new Date().toISOString();
      }

      const { data: document, error } = await supabase
        .from('knowledge_documents')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return { message: 'Document ajouté avec succès', document };
    },

    update: async (id: string, data: any) => {
      const { data: document, error } = await supabase
        .from('knowledge_documents')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { message: 'Document mis à jour avec succès', document };
    },

    delete: async (id: string) => {
      const { error } = await supabase
        .from('knowledge_documents')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { message: 'Document supprimé avec succès' };
    },

    approve: async (id: string) => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      let userId: string;
      if (sessionError || !session || !session.user) {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          throw new Error('Vous devez être connecté');
        }
        userId = authUser.id;
      } else {
        userId = session.user.id;
      }

      const { data: document, error } = await supabase
        .from('knowledge_documents')
        .update({
          status: 'approved',
          approved_by: userId,
          approved_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { message: 'Document approuvé avec succès', document };
    },

    incrementDownload: async (id: string) => {
      const { data: document } = await supabase
        .from('knowledge_documents')
        .select('download_count')
        .eq('id', id)
        .single();

      const newCount = (document?.download_count || 0) + 1;

      const { error } = await supabase
        .from('knowledge_documents')
        .update({ download_count: newCount })
        .eq('id', id);

      if (error) throw error;
      return { download_count: newCount };
    },
  },
};

